// frontend_pixelmoon/src/pages/vip/VipPage.jsx
import { useTheme } from "@/components/context/ThemeContext";
import React, { useEffect, useState } from "react";
import VipCard from "@/components/vip/VipCard";
import {
  readVipStatusCache,
  writeVipStatusCache,
  withVipStatusRequestLock,
  resolveVipStatusNamespace,
} from "@/utils/vipStatusCache";
import { getAccessToken } from "@/utils/authStorage";
import VipPurchaseSection from "./VipPurchaseSection";

const API_BASE = import.meta.env.VITE_API_URL;

const sampleTiers = {
  success: true,
  tiers: [
    {
      _id: "68e79a5aeaca61f6082d3745",
      key: "VIP1",
      name: "Lunar Initiate",
      unlockOrders: 200,
      unlockWindowDays: 30,
      freeValidityDays: 15,
      paidPriceInr: 149,
      paidValidityDays: 30,
      discountPercent: 5,
      multiplier: 1,
      badgeText: "Lunar Initiate",
      badgeColor: "purple",
      sortOrder: 1,
      enabled: true,
      __v: 0,
      createdAt: "2025-10-09T11:19:54.595Z",
      updatedAt: "2025-10-09T11:19:54.595Z",
    },
    {
      _id: "68e79a5aeaca61f6082d3743",
      key: "BASIC",
      name: "Basic VIP",
      description: "Entry-level VIP with basic perks",
      unlockOrders: 0,
      unlockWindowDays: 0,
      freeValidityDays: 0,
      paidPriceInr: 0,
      paidValidityDays: 30,
      discountPercent: 0,
      multiplier: 1,
      badgeColor: "#FFD700",
      sortOrder: 1,
      enabled: true,
      createdAt: "2025-10-09T11:19:54.539Z",
      updatedAt: "2025-10-09T11:19:54.539Z",
      __v: 0,
    },
    {
      _id: "68e79a5aeaca61f6082d3746",
      key: "VIP2",
      name: "Pixel Guardian",
      unlockOrders: 500,
      unlockWindowDays: 90,
      freeValidityDays: 45,
      paidPriceInr: 399,
      paidValidityDays: 90,
      discountPercent: 10,
      multiplier: 1.5,
      badgeText: "Pixel Guardian",
      badgeColor: "blue",
      sortOrder: 2,
      enabled: true,
      __v: 0,
      createdAt: "2025-10-09T11:19:54.595Z",
      updatedAt: "2025-10-09T11:19:54.595Z",
    },
    {
      _id: "68e79a5aeaca61f6082d3747",
      key: "VIP3",
      name: "Celestial Overlord",
      unlockOrders: 1000,
      unlockWindowDays: 180,
      freeValidityDays: 180,
      paidPriceInr: 999,
      paidValidityDays: 365,
      discountPercent: 15,
      multiplier: 2,
      badgeText: "Celestial Overlord",
      badgeColor: "gold",
      sortOrder: 3,
      enabled: true,
      __v: 0,
      createdAt: "2025-10-09T11:19:54.595Z",
      updatedAt: "2025-10-09T11:19:54.595Z",
    },
  ],
};

function VipPage() {
  const [tiers, setTiers] = useState([]);
  const [progress, setProgress] = useState([]);
  const [userVip, setUserVip] = useState(null);
  const [method, setMethod] = useState("matrixsols"); // default
  const theme = useTheme();

  useEffect(() => {
    fetchTiers();
    fetchProgress();
    fetchStatus();
  }, []);

  async function fetchTiers() {
    const res = await fetch(`${API_BASE}/vip/tiers`);
    const j = await res.json();
    if (j.success) setTiers(j.tiers);
    setTiers(sampleTiers.tiers);
  }
  async function fetchProgress() {
    const token = getAccessToken();
    const res = await fetch(`${API_BASE}/vip/progress`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const j = await res.json();
    if (j.success) setProgress(j.progress);
  }
  async function fetchStatus() {
    const cacheNamespace = resolveVipStatusNamespace();
    const cached = readVipStatusCache({ namespace: cacheNamespace });

    if (cached) {
      setUserVip(cached);
      return;
    }

    await withVipStatusRequestLock({ namespace: cacheNamespace }, async () => {
      const token = getAccessToken();
      const res = await fetch(`${API_BASE}/vip/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (j.success) {
        setUserVip(j.status);
        writeVipStatusCache({ value: j.status, namespace: cacheNamespace });
      }
    });
  }

  async function buyTier(tierId) {
    const token = getAccessToken();
    const res = await fetch("/api/v1/vip/purchase", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tierId, method }),
    });
    const j = await res.json();
    if (!j.success) return alert(j.message || "purchase failed");
    if (j.paymentUrl) {
      window.location.href = j.paymentUrl;
      return;
    }
    alert("VIP purchased and activated");
    fetchStatus();
  }

  console.log(tiers);

  return (
    <div className="py-[20px] px-[40px] bg-[var(--primary-color)] text-white">
      <div className={`w-fit mb-[16px] text-[32px] font-bold `}>
        Power-Up Membership
      </div>

      <div className="mb-[16px] text-white">
        <label className="mr-2">Select payment method: </label>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="border-1 rounded bg-white text-gray-900"
        >
          <option value="matrixsols">Matrixsols</option>
          <option value="phonepe">PhonePe</option>
        </select>
      </div>

      <div className="flex flex-column w-full gap-4 ">
        {tiers.map((t, index) => {
          const p = progress.length ?  progress.find((x) => x.tierId === t._id) : {};
          const eligible = p.ordersCount >= (t.unlockOrders || 0);
          const isActive = userVip?.tier && userVip.tier._id === t._id;
          return (
            <VipCard
              key={t._id}
              t={t}
              index={index}
              p={p}
              eligible={eligible}
              isActive={isActive}
            />
          );
        })}
      </div>
    </div>
  );
}

export default VipPage;
